import { httpPost, httpPatchMerge, httpDelete } from './http'
import { getSuppliersFromOrder } from './orders'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import type { IOrderContact } from '~/types/order/models'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'

const apiEntityPath = '/api/ordercontacts'

/**
 * Candidate contacts for the "add contact" picker on an order: the order's company contacts
 * plus the contacts of every supplier used by its MSServices. The suppliers are fetched by
 * order id (they are not a parent of an orderContact); companyId comes from the parent chain
 * (orderContact -> order -> company). Mirrors getAddableContactsForOpportunityPreProcessed.
 */
export async function getAddableContactsForOrderPreProcessed(
  orderId: TEntityId,
  companyId: TEntityId
) {
  // getSuppliersFromOrder is (mis)typed as IOrder but returns the supplier list at runtime.
  const suppliers = (await getSuppliersFromOrder(
    orderId
  )) as unknown as IEntitySelectItem[]
  const supplierIds = (suppliers ?? []).map((s) => s.id)

  const [companyContacts, ...supplierContactLists] = await Promise.all([
    companyId
      ? getContactsForCompanyIdPreProcessed(companyId)
      : Promise.resolve([]),
    ...supplierIds.map((id) => getContactsForSupplierIdPreProcessed(id)),
  ])
  // Re-run generic Contact() so the picker label reads "Name | company/supplier | role".
  return [...companyContacts, ...supplierContactLists.flat()].map((c) =>
    Contact(c)
  )
}

export async function createOrderContact(entity: IOrderContact) {
  const callingMn = createOrderContact.name
  const apiPath = apiEntityPath
  return httpPost<IOrderContact>({
    callingMn,
    apiPath,
    entity,
  })
}

export async function updateOrderContact(
  entityData: Partial<IOrderContact>,
  id: TEntityId
) {
  const callingMn = updateOrderContact.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialOrderContact = Partial<IOrderContact>
  return httpPatchMerge<TPartialOrderContact>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOrderContact(entityData: IOrderContact) {
  const callingMn = deleteOrderContact.name
  const apiPath = apiEntityPath
  return httpDelete<IOrderContact>({
    callingMn,
    apiPath,
    entityData,
  })
}
